import { buildOrderGridQueryString } from "./helpers/buildManagePageQueryString";
import { jumboMediaAPI } from "./service";

import type { GetUserByUserTokenAsyncPayloadT, GetUserByUserTokenAsyncResultT, RegisterUserPayloadT, RegisterUserResultT } from "./models/models";

export const usersAPI = jumboMediaAPI.injectEndpoints({
    endpoints: (build) => ({
        getUsers: build.query({
            // spec-33: resolve-by-name. Each filter lands on the column whose `value` matches the
            // backend field name that UserGridFilters (BL) matches on, instead of the old builder's
            // hardcoded `index === 6` — which silently retargeted the moment the columns array changed.
            // The global search term is URL-encoded here too; it used to be interpolated raw.
            query: ({ columns, page, searchTerm, limit, orderColumnIndex, isDescSortDir, clientId, accessLevel, status }) =>
                `/api/Users/DataTablesSource${buildOrderGridQueryString(
                    columns,
                    page,
                    searchTerm,
                    (page - 1) * limit,
                    limit,
                    orderColumnIndex,
                    isDescSortDir,
                    {
                        ClientId: clientId,
                        AccessLevel: accessLevel,
                        Status: status,
                    }
                )}`,
            providesTags: ['User']
        }),
        getAllUsers: build.query({
            query: (body) => ({
                url: `/api/Users/AdminGet`,
                responseHandler: (res: any) => res.json().then((res: any) => {
                    if (body?.asSelectOptions) {
                        return res.map((item: any) => ({ value: item.Id, label: item.UserName }));
                    } else {
                        return res;
                    }
                })
            }),
            providesTags: ['User']
        }),
        getUsersForSettings: build.query({
            query: body => ({
                url: `/api/Users/UserGetByClientAdmin?pageNumber=${body.page}&itemsPerPage=${body.limit}&filter=${body.filter || ''}&sortingOrder=${body.orderColumnIndex || 'username'}`,
            }),
            transformResponse: (res: any) => ({
                data: res.Users.map((user: any) => ({ ...user, FullName: `${user.FirstName} ${user.LastName}` })),
                recordsFiltered: res.TotalItemCount,
            }),
            providesTags: ['User']
        }),
        getUserById: build.query({
            query: (body) => `/api/Users/UserGet/${body.Id}`,
            providesTags: ['User']
        }),
        getUserByUserTokenAsync: build.query<GetUserByUserTokenAsyncResultT, GetUserByUserTokenAsyncPayloadT>({
            query: (body) => `/api/Users/GetUserByUserTokenAsync?userId=${body.userId}&token=${body.token}`,
            providesTags: ['User']
        }),
        createUser: build.mutation({
            query: (body) => ({
                url: `/api/Users/AdminPost`,
                method: 'POST',
                body,
            }),
            invalidatesTags: ['User', 'Clients'],
        }),
        updateUser: build.mutation({
            query: (body) => {
                return {
                    url: `/api/Users/AdminPut/${body.Id}`,
                    method: 'PUT',
                    body,
                }
            },
            invalidatesTags: ['User', 'Clients'],
            async onQueryStarted({ Id, ...patch }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    usersAPI.util.updateQueryData('getUsers', undefined, (draft) => {
                        // console.log(draft);

                        // Object.assign(draft.data.find((user: any) => user.Id === Id), patch)
                    })
                );
                try {
                    await queryFulfilled
                } catch {
                    patchResult.undo()
                }
            },
        }),
        updateUserData: build.mutation({
            query: (body) => ({
                url: `/api/Users/UserPut/${body.Id}`,
                method: 'PUT',
                body
            }),
            invalidatesTags: ['User'],
        }),
        getNotificationsSettings: build.query({
            query: body => ({
                url: `/api/Users/UserGetNotificationEmails/${body.Id}`
            }),
        }),
        updateNotificationsSettings: build.mutation({
            query: body => ({
                url: `/api/Users/UserPutNotificationEmails/${body.Id}`,
                method: 'PUT',
                body,
            }),
        }),
        deleteUserFromSettings: build.mutation({
            query: body => ({
                url: `/api/Users/UserDeleteByClientAdmin/${body.id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['User'],
        }),
        createUserFromSettings: build.mutation({
            query: body => ({
                url: `/api/Users/UserPostByClientAdmin`,
                method: 'POST',
                body
            }),
            invalidatesTags: ['User'],
        }),
        updateUserFromSettings: build.mutation({
            query: body => ({
                url: `/api/Users/UserPutByClientAdmin/${body.id}`,
                method: 'PUT',
                body
            }),
            invalidatesTags: ['User'],
        }),
        deleteUser: build.mutation({
            query: (body) => ({
                url: `/api/Users/AdminDelete/${body.id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['User', 'Clients'],
        }),
        changePassword: build.mutation({
            query: body => ({
                url: `/api/Users/UserChangePassword/${body.Id}`,
                method: 'POST',
                body,
            }),
        }),
        sendInitPasswordEmail: build.mutation({
            query: body => ({
                url: `/api/Users/UserSendInitPasswordEmailByClientAdmin/${body.id}`,
                method: 'POST'
            }),
        }),
        // GlobalAdmin's Manage → Users page. The client-admin endpoint above accepts client admins only, so a
        // GlobalAdmin got 401 and no mail went out (JMMEDIAT-875 QA). The backend's admin twin always existed.
        adminSendInitPasswordEmail: build.mutation({
            query: body => ({
                url: `/api/Users/AdminSendInitPasswordEmail/${body.id}`,
                method: 'POST'
            }),
        }),
        registerUser: build.mutation<RegisterUserResultT, RegisterUserPayloadT>({
            query: (body) => ({
              url: `/api/Users/RegisterUserByToken`,
              method: "POST",
              body,
            }),
          }),
    }),
})

export const {
    useGetUsersQuery,
    useCreateUserMutation,
    useUpdateUserMutation,
    useDeleteUserMutation,
    useGetAllUsersQuery,
    useLazyGetAllUsersQuery,
    useLazyGetUserByIdQuery,
    useGetUserByIdQuery,
    useUpdateUserDataMutation,
    useGetNotificationsSettingsQuery,
    useUpdateNotificationsSettingsMutation,
    useChangePasswordMutation,
    useGetUsersForSettingsQuery,
    useDeleteUserFromSettingsMutation,
    useCreateUserFromSettingsMutation,
    useUpdateUserFromSettingsMutation,
    useSendInitPasswordEmailMutation,
    useAdminSendInitPasswordEmailMutation,
    useRegisterUserMutation,
    useGetUserByUserTokenAsyncQuery,
} = usersAPI;